import { useRef, useState } from "react";
import api from "@/lib/api";
import {
  UploadSimple,
  X,
  Image as ImageIcon,
  VideoCamera,
  PlayCircle,
} from "@phosphor-icons/react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const IMAGE_TYPES = "image/png,image/jpeg,image/jpg,image/webp,image/gif";
const VIDEO_TYPES = "video/mp4,video/quicktime,video/webm";

/**
 * Universal media uploader (image + optional video).
 * value: URL string ('/api/uploads/abc.png' or full http(s))
 * onChange(url, kind): called with new URL and "photo" | "video" | "" (cleared)
 * accept: "image" | "video" | "any"
 */
export default function MediaUpload({
  value,
  onChange,
  accept = "image",
  testid = "media-upload",
  helpText,
}) {
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState("");
  const inputRef = useRef(null);

  const fullUrl = value
    ? value.startsWith("/")
      ? BACKEND_URL + value
      : value
    : null;

  const isVideo = value && /\.(mp4|mov|webm)$/i.test(value);

  const acceptAttr =
    accept === "video"
      ? VIDEO_TYPES
      : accept === "any"
      ? IMAGE_TYPES + "," + VIDEO_TYPES
      : IMAGE_TYPES;

  const handleFile = async (file) => {
    if (!file) return;
    setErr("");
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/upload", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const kind = file.type.startsWith("video/") ? "video" : "photo";
      onChange(data.url, kind);
    } catch (e) {
      setErr(e.response?.data?.detail || "Ошибка загрузки");
    } finally {
      setUploading(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (file) handleFile(file);
  };

  return (
    <div data-testid={testid} className="space-y-2">
      <div className="flex items-start gap-3">
        {fullUrl ? (
          <div className="relative w-28 h-28 rounded-md overflow-hidden border border-zinc-800 bg-zinc-900 group">
            {isVideo ? (
              <>
                <video
                  src={fullUrl}
                  className="w-full h-full object-cover"
                  muted
                />
                <div className="absolute inset-0 flex items-center justify-center bg-zinc-950/40 text-zinc-100 pointer-events-none">
                  <PlayCircle size={32} weight="fill" />
                </div>
              </>
            ) : (
              <img
                src={fullUrl}
                alt="cover"
                className="w-full h-full object-cover"
              />
            )}
            <button
              type="button"
              onClick={() => onChange("", "")}
              data-testid={`${testid}-remove`}
              className="absolute top-1 right-1 bg-zinc-950/80 hover:bg-red-500/90 rounded p-1 text-zinc-200"
            >
              <X size={12} weight="bold" />
            </button>
          </div>
        ) : (
          <div
            onClick={() => inputRef.current?.click()}
            onDragOver={(e) => e.preventDefault()}
            onDrop={onDrop}
            className="w-28 h-28 border border-dashed border-zinc-700 rounded-md flex flex-col items-center justify-center text-zinc-500 hover:border-blue-500/50 hover:text-blue-400 transition-colors cursor-pointer"
          >
            {accept === "video" ? (
              <VideoCamera size={20} />
            ) : (
              <ImageIcon size={20} />
            )}
            <span className="text-[10px] mt-1 uppercase tracking-wider">
              нет файла
            </span>
          </div>
        )}
        <div className="flex-1">
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
            data-testid={`${testid}-btn`}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-sm rounded-md border border-zinc-800 hover:border-blue-500/50 hover:text-blue-400 disabled:opacity-50"
          >
            <UploadSimple size={14} weight="bold" />
            {uploading
              ? "Загрузка…"
              : value
              ? "Заменить файл"
              : accept === "video"
              ? "Загрузить видео"
              : accept === "any"
              ? "Загрузить фото или видео"
              : "Загрузить фото"}
          </button>
          <div className="text-[11px] text-zinc-500 mt-1.5 leading-snug">
            {helpText ||
              (accept === "video"
                ? "MP4 / MOV / WebM, до 20 MB. Файл будет отправлен боту по URL."
                : accept === "any"
                ? "PNG / JPG / WebP до 8 MB или MP4 / MOV / WebM до 20 MB."
                : "PNG / JPG / WebP, до 8 MB.")}
          </div>
          {err && (
            <div
              className="text-xs text-red-400 mt-1"
              data-testid={`${testid}-error`}
            >
              {err}
            </div>
          )}
        </div>
      </div>
      <input
        ref={inputRef}
        type="file"
        accept={acceptAttr}
        className="hidden"
        onChange={(e) => handleFile(e.target.files?.[0])}
      />
    </div>
  );
}
